import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Label } from "design-system/atoms/Label"
import { FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { McpCapabilityConfigSchema, TestMcpResponseSchema } from "schema"
import { z } from "zod"

type MCPCapabilityFormType = z.infer<typeof McpCapabilityConfigSchema>
type DiscoveredTool = z.infer<typeof TestMcpResponseSchema>["tools"][number]
type Props = {
  isLoading: boolean
  discoveredTools: Array<DiscoveredTool>
}

export function MCPToolsAccordion({ isLoading, discoveredTools }: Props) {
  const { control, formState } = useFormContext<MCPCapabilityFormType>()
  const { t } = useTranslation()

  const hasError = Boolean(formState.errors.tools)

  if (discoveredTools.length === 0) {
    return (
      <div className="border rounded-lg bg-secondary p-4">
        <p className="text-sm text-muted-foreground">
          {t("capabilities.mcpConfig.enabledTools.empty")}
        </p>
      </div>
    )
  }

  return (
    <Accordion
      type="single"
      defaultValue="tools"
      collapsible
      className={cn("border rounded-lg bg-secondary", hasError && "border-destructive")}
    >
      <AccordionItem value="tools">
        <AccordionTrigger className="text-lg">
          {t("capabilities.mcpConfig.enabledTools.label")}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">
            {t("capabilities.mcpConfig.enabledTools.description")}
          </p>
          <FormField
            control={control}
            name="tools"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-col gap-3">
                  {discoveredTools.map((tool) => {
                    const toolInForm = field.value?.find((t) => t.name === tool.name)
                    const isChecked = toolInForm?.enabled ?? false
                    return (
                      <Label
                        key={tool.name}
                        htmlFor={`tool-${tool.name}`}
                        className={cn(
                          "flex items-start gap-2 rounded-lg p-3 cursor-pointer transition-colors hover:bg-accent border",
                          isChecked ? "border-primary bg-secondary" : "border-muted",
                        )}
                      >
                        <Checkbox
                          id={`tool-${tool.name}`}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const current = field.value || []
                            if (checked) {
                              // Add or update tool with enabled: true
                              const existingIndex = current.findIndex((t) => t.name === tool.name)
                              if (existingIndex >= 0) {
                                const updated = [...current]
                                if (updated[existingIndex]) {
                                  updated[existingIndex] = {
                                    ...updated[existingIndex],
                                    enabled: true,
                                  }
                                }
                                field.onChange(updated)
                              } else {
                                field.onChange([...current, { ...tool, enabled: true }])
                              }
                            } else {
                              // Set enabled: false
                              const updated = current.map((t) =>
                                t.name === tool.name ? { ...t, enabled: false } : t,
                              )
                              field.onChange(updated)
                            }
                          }}
                          className="m-1"
                          disabled={isLoading}
                        />
                        <div className="flex-1 flex flex-col gap-1">
                          <span className="text-base">{tool.name}</span>
                          {tool.description && (
                            <p className="text-xs text-muted-foreground">{tool.description}</p>
                          )}
                        </div>
                      </Label>
                    )
                  })}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
